import { inject, Injectable, NgZone } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';

import { environment } from 'src/environments/environment';
import { ClientContextService } from '../client/client-context.service';
import { CredentialsService } from '../../authentication/services/credentials.service';
import { TokenRefreshService } from '../../authentication/services/token-refresh.service';

/**
 * Corps d'un tour de chat relay. Le front n'envoie que le dernier message :
 * l'historique est reconstruit côté serveur depuis le `sessionId`.
 */
export interface AgentChatRequest {
    /** Requis pour une nouvelle conversation (sessionId absent) ; ignoré en reprise. */
    agent?: string;
    /** Absent ⇒ nouvelle conversation ; fourni ⇒ reprise de la session. */
    sessionId?: string | null;
    /** Nouvelle saisie utilisateur. */
    message: string;
}

/** Détail des tokens d'un tour ou d'un cumul de session (cf. §4.1). */
export interface AgentUsageTokens {
    /** Tokens d'entrée TOTAUX (prompt + historique + outils). */
    input: number;
    /** Sous-ensemble d'`input` servi depuis le cache (NE PAS additionner à `input`). */
    cachedInput: number;
    /** Tokens de réponse. */
    output: number;
    /** Tokens de raisonnement (facturés comme de l'output). */
    thoughts: number;
    /** input + output + thoughts (cachedInput non ajouté : sous-ensemble d'input). */
    total: number;
}

/** Coût BRUT (sans marge) — montants en string décimale pour préserver la précision. */
export interface AgentUsageCost {
    input: string;
    output: string;
    /** Recherches sur internet : poste à part, déjà compris dans `total` (`"0"` sans recherche). */
    webSearch: string;
    /** input + output + webSearch. */
    total: string;
    /** Devise des tarifs fournisseurs (ex. `USD`). */
    currency: string;
}

/** Consommation de tokens + valorisation brute d'un tour ou d'un cumul de session. */
export interface AgentUsage {
    tokens: AgentUsageTokens;
    /** `null` si le Prompt/modèle n'est plus résoluble (historique découplé). */
    cost: AgentUsageCost | null;
}

/** Événement SSE émis par le relay (cf. AGENT_IA_FRONT_INTEGRATION §4). */
export type AgentStreamEvent =
    | { type: 'session'; sessionId: string; turnIndex: number }
    | { type: 'token'; text: string }
    | { type: 'thought'; text: string }
    | { type: 'tool'; name: string; label: string; phase: 'start' | 'end' }
    | ({ type: 'action' } & Record<string, unknown>)
    | { type: 'usage'; modelVersion: string; turn: AgentUsage; session: AgentUsage }
    | { type: 'done' }
    | { type: 'error'; outcome?: 'tool_limit' | 'error'; message: string };

const CHAT_URL = '/agent/chat/';

/**
 * Couche transport du chat IA en mode relay.
 *
 * Le front est « bête » : il POST le dernier message et lit le flux SSE. Toute
 * l'orchestration (Gemini, boucle d'outils) et l'historique sont côté Django.
 * Comme c'est un POST, on ne peut pas utiliser EventSource : on lit le corps en
 * streaming via fetch + ReadableStream.
 */
@Injectable({
    providedIn: 'root'
})
export class AgentRelayClient {

    private _clientContextService = inject(ClientContextService);
    private _credentialsService = inject(CredentialsService);
    private _ngZone = inject(NgZone);
    private _tokenRefreshService = inject(TokenRefreshService);

    /**
     * Ouvre un tour de conversation. Émet les events SSE au fil de l'eau (dans la
     * zone Angular), puis complète. La désinscription annule la requête en cours.
     */
    public chat$(request: AgentChatRequest): Observable<AgentStreamEvent> {
        return new Observable<AgentStreamEvent>(subscriber => {
            const controller = new AbortController();
            this._run(request, controller.signal, subscriber);
            return () => controller.abort();
        });
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private async _run(
        request: AgentChatRequest,
        signal: AbortSignal,
        subscriber: { next: (e: AgentStreamEvent) => void; complete: () => void }
    ): Promise<void> {
        const emit = (event: AgentStreamEvent) => this._ngZone.run(() => subscriber.next(event));
        const complete = () => this._ngZone.run(() => subscriber.complete());

        try {
            let response = await this._post(request, signal, this._credentialsService.credentials?.access);

            // L'access token expire en cinq minutes et ce flux ne passe pas par
            // HttpClient : il ne bénéficie pas du rejeu de l'intercepteur, on le
            // fait donc ici. Un 401 signifie que le serveur n'a rien traité, le
            // rejeu ne peut pas dupliquer le tour.
            if (response.status === 401) {
                const token = await firstValueFrom(this._tokenRefreshService.refresh$());
                response = await this._post(request, signal, token);
            }

            if (!response.ok || !response.body) {
                emit({ type: 'error', message: await this._readError(response) });
                complete();
                return;
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });

                // Découpe sur le séparateur d'events SSE
                const parts = buffer.split('\n\n');
                buffer = parts.pop() ?? '';
                for (const block of parts) {
                    const line = block.split('\n').find(l => l.startsWith('data:'));
                    if (!line) continue;
                    try {
                        emit(JSON.parse(line.slice(5).trim()) as AgentStreamEvent);
                    } catch {
                        // Ligne non-JSON ignorée
                    }
                }
            }

            complete();
        } catch {
            if (signal.aborted) {
                complete();
                return;
            }
            emit({ type: 'error', message: 'Connexion à l\'assistant interrompue.' });
            complete();
        }
    }

    /**
     * Nouvelle conversation ⇒ on envoie `agent` (sans sessionId).
     * Reprise ⇒ on envoie `sessionId` (l'agent est celui de la session).
     */
    private _buildBody(request: AgentChatRequest): Record<string, unknown> {
        const body: Record<string, unknown> = { message: request.message };
        if (request.sessionId) {
            body['sessionId'] = request.sessionId;
        } else {
            body['agent'] = request.agent;
        }
        return body;
    }

    /** Poste le tour au relay avec le token fourni. */
    private _post(request: AgentChatRequest, signal: AbortSignal, token: string | undefined): Promise<Response> {
        // Ce flux ne passe pas par HttpClient : il pose le contexte lui-même,
        // depuis la même source que l'intercepteur (#173).
        const clientId = this._clientContextService.clientId;

        return fetch(`${environment.serverUrl}${CHAT_URL}`, {
            method: 'POST',
            signal,
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                ...(clientId !== null ? { 'context-client': clientId.toString() } : {}),
            },
            body: JSON.stringify(this._buildBody(request)),
        });
    }

    private async _readError(response: Response): Promise<string> {
        try {
            const body = await response.json();
            if (body?.error) return body.error;
        } catch {
            // Corps non-JSON
        }
        return `Erreur ${response.status}.`;
    }
}
